'use client';

import Image from 'next/image';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Quote,
  Building2,
  TrendingUp,
  Users,
  Star,
} from 'lucide-react';
import { getFeaturePage, getRelatedFeatures } from '@/lib/feature-pages';
import { FEATURE_HERO_IMAGES, FEATURE_SECTION_IMAGES } from '@/lib/marketing-images';

function splitIntoParagraphs(text: string, wordsPerParagraph = 80): string[] {
  const words = text.split(' ');
  const paragraphs: string[] = [];
  let current: string[] = [];

  for (const word of words) {
    current.push(word);
    if (current.length >= wordsPerParagraph && word.includes('.')) {
      paragraphs.push(current.join(' '));
      current = [];
    }
  }
  if (current.length > 0) paragraphs.push(current.join(' '));
  return paragraphs;
}

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function FeatureDetailPage() {
  const params = useParams();
  const slug = params.slug as string;
  const feature = getFeaturePage(slug) as
    | (ReturnType<typeof getFeaturePage> & {
        extendedDescription?: string;
        subFunctions?: { title: string; description: string; icon?: string }[];
        caseStudy?: {
          company: string;
          industry: string;
          challenge: string;
          solution: string;
          results: { metric: string; improvement: string }[];
          quote: string;
          author: string;
          role: string;
        };
        seoTitle?: string;
        seoDescription?: string;
        seoKeywords?: string[];
      })
    | undefined;

  if (!feature) {
    return (
      <div className="gradient-hero grid-bg flex min-h-[80vh] flex-col items-center justify-center text-center px-6">
        <div className="glass-card rounded-2xl p-12 max-w-md">
          <h1 className="text-2xl font-bold text-white">Feature not found</h1>
          <p className="mt-3 text-slate-300">
            The feature page you are looking for does not exist.
          </p>
          <Link
            href="/features"
            className="btn-neon mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Features
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedFeatures(feature.relatedSlugs);
  const Icon = feature.icon;
  const extParagraphs = feature.extendedDescription
    ? splitIntoParagraphs(feature.extendedDescription)
    : [];
  const featureHeroImage =
    FEATURE_HERO_IMAGES[slug as keyof typeof FEATURE_HERO_IMAGES] ?? {
      src: '/images/marketing/features-overview-telematics-platform-features.jpg',
      alt: 'telematics platform features',
    };

  return (
    <>
      {/* ── 1. Hero ──────────────────────────────────────────── */}
      <section className="gradient-hero grid-bg relative overflow-hidden py-24 lg:py-32">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse 60% 40% at 50% 0%, var(--neon-cyan) 0%, transparent 60%)`,
          }}
        />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <Link
            href="/features"
            className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition hover:text-neon-cyan"
          >
            <ArrowLeft className="h-4 w-4" /> All Features
          </Link>

          <div className="grid items-center gap-16 lg:grid-cols-2">
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-neon-cyan/20 to-neon-purple/20 px-4 py-2 backdrop-blur">
                <Icon className="h-6 w-6 text-neon-cyan" />
                <span className="text-sm font-medium text-neon-cyan">
                  {feature.title}
                </span>
              </div>

              <div className="glass-card inline-block rounded-full px-4 py-1.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-neon-purple">
                  {feature.tagline}
                </span>
              </div>

              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                {feature.title}
              </h1>

              <p className="max-w-lg text-lg leading-relaxed text-slate-300">
                {feature.description}
              </p>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <Link
                  href={`/signup?feature=${feature.slug}`}
                  className="btn-neon inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-sm font-semibold"
                >
                  Start Using {feature.title.split(' ')[0]}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href="/pricing"
                  className="btn-neon-outline inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-sm font-semibold"
                >
                  View Pricing
                </Link>
              </div>
            </motion.div>

            {/* Hero Image + Stats Grid */}
            <div className="space-y-6">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={featureHeroImage.src}
                  alt={featureHeroImage.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {feature.stats.map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
                    className="glass-card rounded-2xl p-6 text-center"
                  >
                    <p className="text-gradient text-3xl font-bold">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-sm text-slate-300">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Extended Description ──────────────────────────── */}
      {feature.extendedDescription && (
        <section className="gradient-section py-20 lg:py-28">
          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-cyan/20 bg-neon-cyan/5 px-4 py-1.5">
                <Star className="h-3.5 w-3.5 text-neon-cyan" />
                <span className="text-xs font-semibold uppercase tracking-wider text-neon-cyan">
                  Deep Dive
                </span>
              </div>

              <h2 className="mb-8 text-3xl font-bold text-white sm:text-4xl">
                Understanding{' '}
                <span className="text-gradient">{feature.title}</span> in Detail
              </h2>

              <div className="mb-8 glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={FEATURE_SECTION_IMAGES.deepDive.src}
                  alt={FEATURE_SECTION_IMAGES.deepDive.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                />
              </div>

              <div className="space-y-6">
                {extParagraphs.map((p, i) => (
                  <motion.p
                    key={i}
                    {...fadeUp}
                    transition={{ delay: i * 0.1, duration: 0.4 }}
                    className="text-base leading-relaxed text-slate-300"
                  >
                    {p}
                  </motion.p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── 3. Sub-Functions ─────────────────────────────────── */}
      {feature.subFunctions && feature.subFunctions.length > 0 && (
        <section className="gradient-section-purple py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.5 }}
              className="mb-16 text-center"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-neon-purple/20 bg-neon-purple/5 px-4 py-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-neon-purple" />
                <span className="text-xs font-semibold uppercase tracking-wider text-neon-purple">
                  Core Sub-Functions
                </span>
              </div>
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                What&rsquo;s Inside{' '}
                <span className="text-gradient-warm">{feature.title}</span>
              </h2>
            </motion.div>

            <div className="mb-10 glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={FEATURE_SECTION_IMAGES.subFunctions.src}
                alt={FEATURE_SECTION_IMAGES.subFunctions.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {feature.subFunctions.slice(0, 6).map((fn, i) => (
                <motion.div
                  key={fn.title}
                  {...fadeUp}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  className="glass-card group relative rounded-2xl p-8 transition-all hover:border-neon-cyan/20"
                >
                  <span className="absolute top-4 right-4 text-sm font-mono font-bold text-neon-cyan/40">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-neon-cyan/10">
                    <Icon className="h-5 w-5 text-neon-cyan" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-white">
                    {fn.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-300">
                    {fn.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 4. Key Capabilities ──────────────────────────────── */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-16 max-w-2xl text-center"
          >
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Key Capabilities
            </h2>
            <p className="mt-3 text-base text-slate-300">
              Everything you need from {feature.title.toLowerCase()}, built into
              the platform.
            </p>
          </motion.div>

          <div className="mb-10 glass-card-heavy rounded-3xl p-2 border border-white/10">
            <Image
              src={FEATURE_SECTION_IMAGES.capabilities.src}
              alt={FEATURE_SECTION_IMAGES.capabilities.alt}
              width={1600}
              height={900}
              className="h-auto w-full rounded-2xl object-cover"
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            {feature.capabilities.map((cap, i) => (
              <motion.div
                key={cap.title}
                {...fadeUp}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="glass-card rounded-2xl p-8 transition-all hover:border-neon-cyan/20"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-neon-cyan/10 text-neon-cyan">
                  <Check className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {cap.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  {cap.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Case Study ────────────────────────────────────── */}
      {feature.caseStudy && (
        <section className="gradient-section-purple py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
              <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-neon-purple/20 bg-neon-purple/5 px-4 py-1.5">
                <Building2 className="h-3.5 w-3.5 text-neon-purple" />
                <span className="text-xs font-semibold uppercase tracking-wider text-neon-purple">
                  Case Study
                </span>
              </div>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="glass-card-heavy rounded-3xl p-8 sm:p-12"
            >
              <div className="mb-8 glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={FEATURE_SECTION_IMAGES.caseStudy.src}
                  alt={FEATURE_SECTION_IMAGES.caseStudy.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                />
              </div>

              <div className="mb-8 flex flex-wrap items-center gap-3">
                <h3 className="text-2xl font-bold text-white">
                  {feature.caseStudy.company}
                </h3>
                <span className="rounded-full border border-neon-amber/30 bg-neon-amber/10 px-3 py-1 text-xs font-semibold text-neon-amber">
                  {feature.caseStudy.industry}
                </span>
              </div>

              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-neon-magenta" />
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-neon-magenta">
                      The Challenge
                    </h4>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-300">
                    {feature.caseStudy.challenge}
                  </p>
                </div>
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-neon-cyan" />
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-neon-cyan">
                      Our Solution
                    </h4>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-300">
                    {feature.caseStudy.solution}
                  </p>
                </div>
              </div>

              {/* Results Metrics */}
              {feature.caseStudy.results && feature.caseStudy.results.length > 0 && (
                <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {feature.caseStudy.results.map((r, i) => (
                    <motion.div
                      key={r.metric}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.1 }}
                      className="glass-card rounded-xl p-4 text-center"
                    >
                      <p className="text-gradient-warm text-2xl font-bold">
                        {r.improvement}
                      </p>
                      <p className="mt-1 text-xs text-slate-300">{r.metric}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Blockquote */}
              {feature.caseStudy.quote && (
                <div className="mt-10 border-l-2 border-neon-purple/30 pl-6">
                  <Quote className="mb-3 h-6 w-6 text-neon-purple/50" />
                  <blockquote className="text-lg italic leading-relaxed text-slate-300">
                    &ldquo;{feature.caseStudy.quote}&rdquo;
                  </blockquote>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neon-purple/20">
                      <Users className="h-4 w-4 text-neon-purple" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {feature.caseStudy.author}
                      </p>
                      <p className="text-xs text-slate-300">
                        {feature.caseStudy.role}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* ── 6. Related Features ──────────────────────────────── */}
      {related.length > 0 && (
        <section className="border-t border-white/10 py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.h3
              {...fadeUp}
              transition={{ duration: 0.5 }}
              className="mb-10 text-center text-2xl font-bold text-white"
            >
              Related Features
            </motion.h3>

            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((rel, i) => (
                <motion.div
                  key={rel.slug}
                  {...fadeUp}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                >
                  <Link
                    href={`/features/${rel.slug}`}
                    className="group block"
                  >
                    <div className="glass-card rounded-2xl p-6 transition-all hover:border-neon-cyan/20">
                      <div className="mb-3 inline-flex rounded-lg bg-neon-cyan/10 p-2 text-neon-cyan transition-colors group-hover:bg-neon-cyan/20">
                        <rel.icon className="h-5 w-5" />
                      </div>
                      <h4 className="font-semibold text-white">
                        {rel.title}
                      </h4>
                      <p className="mt-1 text-sm text-slate-300">
                        {rel.tagline}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 7. CTA Banner ────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 lg:py-24">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,212,255,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 60%, rgba(168,85,247,0.05) 0%, transparent 50%)',
          }}
        />
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
            <div className="mx-auto mb-8 max-w-3xl glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={FEATURE_SECTION_IMAGES.cta.src}
                alt={FEATURE_SECTION_IMAGES.cta.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
              />
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Ready to get started with{' '}
              <span className="text-gradient">{feature.title}</span>?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">
              Start your 14-day free trial today. No credit card required.
            </p>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                href="/signup"
                className="btn-neon inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold"
              >
                Start Free Trial
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="/pricing"
                className="btn-neon-outline inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
